import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";
import OwnerPersonDrawer from "./center/OwnerPersonDrawer";
import OwnerMovementImportDialog from "./center/OwnerMovementImportDialog";
import OwnerRecurringFinanceDialog from "./center/OwnerRecurringFinanceDialog";
import OwnerRecurringRecordDialog from "./center/OwnerRecurringRecordDialog";
import OwnerRecurringFromTransactionDialog from "./center/OwnerRecurringFromTransactionDialog";
import OwnerProposalQuickDialog from "./center/OwnerProposalQuickDialog";
import OwnerChatGptExportDialog from "./center/OwnerChatGptExportDialog";
import {
  OwnerAccountDialog,
  OwnerClientDialog,
  OwnerCollectDialog,
  OwnerCommitmentDialog,
  OwnerFinanceDialog,
  OwnerInitiativeDialog,
  OwnerInteractionDialog,
} from "./center/OwnerQuickDialogs";
import { compactActionSx, money, relativeDate, surfaceSx } from "./center/ownerCenterUi";
import OwnerWorkspaceNavigation from "./components/OwnerWorkspaceNavigation";
import OwnerSetupOnboarding from "./components/OwnerSetupOnboarding";
import OwnerVoiceOperatorPanel from "./voice/OwnerVoiceOperatorPanel";
import OwnerInitiativeDrawer from "./center/OwnerInitiativeDrawer";
import { OwnerVoiceOperatorProvider } from "./voice/OwnerVoiceOperatorContext";

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function dueTime(item) {
  const value = item?.scheduledStartAt || item?.dueAt;
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : Number.POSITIVE_INFINITY;
}

function isOverdue(item) {
  const at = dueTime(item);
  return Number.isFinite(at) && at < Date.now();
}

function MiniStat({ label, value, tone = "text.primary" }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: "text.secondary", fontWeight: 800, fontSize: 11.5, lineHeight: 1.2 }}>{label}</Typography>
      <Typography noWrap sx={{ mt: .2, color: tone, fontWeight: 1000, fontSize: { xs: 18, xl: 21 }, letterSpacing: "-.025em" }}>{value}</Typography>
    </Box>
  );
}

function EmptyLine({ children }) {
  return <Typography sx={{ py: 1.1, color: "text.secondary", fontWeight: 750, fontSize: 13 }}>{children}</Typography>;
}

export default function OwnerPanelPage() {
  const navigate = useNavigate();
  const [center, setCenter] = React.useState(null);
  const [assistant, setAssistant] = React.useState(null);
  const [assistantCurrency, setAssistantCurrency] = React.useState("ARS");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [dialog, setDialog] = React.useState("");
  const [sourceEntry, setSourceEntry] = React.useState(null);
  const [saleToCollect, setSaleToCollect] = React.useState(null);
  const [relationPerson, setRelationPerson] = React.useState(null);
  const [recurringToRecord, setRecurringToRecord] = React.useState(null);
  const [transactionToRecurring, setTransactionToRecurring] = React.useState(null);
  const [selectedPerson, setSelectedPerson] = React.useState(null);
  const [personContext, setPersonContext] = React.useState(null);
  const [personLoading, setPersonLoading] = React.useState(false);
  const [personError, setPersonError] = React.useState("");
  const [chatGptPerson, setChatGptPerson] = React.useState(null);
  const [selectedInitiative, setSelectedInitiative] = React.useState(null);
  const voicePanelAnchorRef = React.useRef(null);

  const load = React.useCallback(async () => {
    try {
      setError("");
      const [centerResponse, assistantResponse] = await Promise.all([
        ownerApi.center(),
        ownerApi.assistant({ currency: assistantCurrency }),
      ]);
      setCenter(centerResponse.center);
      setAssistant(assistantResponse.assistant);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar el Panel rápido."));
    } finally {
      setLoading(false);
    }
  }, [assistantCurrency]);

  React.useEffect(() => { load(); }, [load]);

  const loadPersonContext = React.useCallback(async (person, { reset = true } = {}) => {
    const personId = person?.id || person?._id;
    if (!personId) return;
    if (reset) setPersonContext(null);
    setPersonLoading(true);
    setPersonError("");
    try {
      const response = await ownerApi.personContext(personId);
      setPersonContext(response.context);
    } catch (err) {
      setPersonError(formatApiError(err, "No pude cargar la ficha de esta persona."));
    } finally {
      setPersonLoading(false);
    }
  }, []);

  const openPerson = (person) => {
    setSelectedPerson(person);
    loadPersonContext(person);
  };

  const closeDialogs = () => {
    setDialog("");
    setSourceEntry(null);
    setSaleToCollect(null);
    setRelationPerson(null);
    setRecurringToRecord(null);
    setTransactionToRecurring(null);
  };

  const refreshAfterAction = async () => {
    await load();
    if (selectedPerson) await loadPersonContext(selectedPerson, { reset: false });
  };

  const completeCommitment = async (item) => {
    try {
      setError("");
      await ownerApi.updateCommitment(idOf(item), { status: "done" });
      await refreshAfterAction();
    } catch (err) {
      setError(formatApiError(err, "No pude completar el pendiente."));
    }
  };

  if (loading && !center) return <DashboardLoadingState label="Preparando tu Panel rápido…" minHeight={420} />;

  const personFromVoiceContext = (context) => context?.type === "person" ? context.entity : null;
  const withPerson = (context, nextDialog) => {
    const person = personFromVoiceContext(context);
    setRelationPerson(person || null);
    setDialog(nextDialog);
  };

  const voiceActions = {
    finance: { label: "movimiento", formId: "finance", aliases: ["movimiento", "gasto", "ingreso", "transferencia", "registrar plata", "registrar dinero"], ack: "Registrar movimiento.", run: () => setDialog("finance") },
    client: { label: "persona", formId: "client", resetContext: true, aliases: ["persona", "cliente nuevo", "prospecto nuevo", "nuevo cliente", "nueva persona", "cargar cliente"], ack: "Nueva persona.", run: () => { setRelationPerson(null); setDialog("client"); } },
    proposal: { label: "propuesta", formId: "proposal", aliases: ["propuesta", "presupuesto", "cotizacion", "venta posible", "dinero posible"], ack: (context) => personFromVoiceContext(context) ? `Propuesta para ${personFromVoiceContext(context).name}.` : "Nueva propuesta.", run: (context) => withPerson(context, "proposal") },
    commitment: { label: "seguimiento", formId: "commitment", aliases: ["seguimiento", "tarea", "recordatorio", "turno", "agendar", "pendiente", "proximo paso"], ack: (context) => personFromVoiceContext(context) ? `Seguimiento para ${personFromVoiceContext(context).name}.` : "Nuevo compromiso.", run: (context) => withPerson(context, "commitment") },
    recurring: { label: "mensual", formId: "recurring", aliases: ["mensual", "venta mensual", "servicio mensual", "cobro mensual", "importe mensual", "suscripcion"], ack: (context) => personFromVoiceContext(context) ? `Mensual para ${personFromVoiceContext(context).name}.` : "Nuevo mensual.", run: (context) => withPerson(context, "recurring") },
    interaction: { label: "nota", formId: "interaction", aliases: ["nota", "anotacion", "contacto", "registrar contacto", "conversacion", "que hablamos"], ack: (context) => personFromVoiceContext(context) ? `Anotación para ${personFromVoiceContext(context).name}.` : "Registrar contacto.", run: (context) => withPerson(context, "interaction") },
    initiative: { label: "proyecto", formId: "initiative", aliases: ["proyecto", "frente", "iniciativa", "crear proyecto"], ack: (context) => personFromVoiceContext(context) ? `Proyecto para ${personFromVoiceContext(context).name}.` : "Nuevo proyecto.", run: (context) => withPerson(context, "initiative") },
    calendar: { label: "calendario", aliases: ["calendario", "agenda", "ver agenda", "ver calendario"], ack: "Voy al calendario.", run: () => navigate("/dashboard/owner/calendario") },
    clients: { label: "clientes", aliases: ["clientes", "personas", "crm", "ver clientes", "panel de clientes"], ack: "Voy a clientes.", run: () => navigate("/dashboard/owner/clientes") },
    stats: { label: "estadísticas", aliases: ["estadisticas", "estadistica", "futuro", "historico", "ver finanzas"], ack: "Voy a estadísticas.", run: () => navigate("/dashboard/owner/estadisticas") },
  };

  const initiatives = center?.life?.initiatives || [];
  const commitments = [...(center?.life?.commitments || [])]
    .filter((item) => item.status !== "done")
    .sort((a, b) => {
      if (isOverdue(a) !== isOverdue(b)) return isOverdue(a) ? -1 : 1;
      return dueTime(a) - dueTime(b);
    });
  const primary = commitments[0] || null;
  const nextItems = commitments.slice(primary ? 1 : 0, 5);
  const peopleById = new Map((center?.people?.focus || []).map((person) => [idOf(person), person]));
  const primaryPerson = primary ? peopleById.get(idOf(primary.clientId)) : null;
  const currentQuestion = assistant?.questions?.[0] || null;
  const currency = assistantCurrency || assistant?.currency || "ARS";
  const available = Number(center?.finance?.totalsByCurrency?.[currency] || 0);
  const month = center?.finance?.monthByCurrency?.[currency] || { income: 0, expense: 0, net: 0 };
  const outstanding = Number(center?.commercial?.outstandingByCurrency?.[currency] || 0);
  const gap = Number(assistant?.economy?.uncoveredGap || 0);
  const inboxCount = Number(center?.life?.pendingInboxCount || 0);
  const overdueCount = Number(center?.life?.overdueCommitmentCount || 0);
  const attentionCount = Number(center?.people?.attentionCount || 0);
  const statusLabel = overdueCount > 0 ? `${overdueCount} vencido${overdueCount === 1 ? "" : "s"}` : gap > 0 ? "Meta abierta" : "En orden";
  const statusColor = overdueCount > 0 ? "warning" : gap > 0 ? "default" : "success";

  const onboardingAction = (stepId) => {
    if (stepId === "money") return setDialog("account");
    if (stepId === "history") return setDialog("import");
    if (stepId === "recurring") return setDialog("recurring");
    if (stepId === "people") return setDialog("client");
    if (stepId === "followup") return setDialog("commitment");
    if (stepId === "calendar") return navigate("/dashboard/owner/calendario");
    if (stepId === "future") return navigate("/dashboard/owner/estadisticas?vista=futuro");
    if (stepId === "voice") {
      voicePanelAnchorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      window.setTimeout(() => {
        const button = voicePanelAnchorRef.current?.querySelector?.("button");
        button?.focus?.();
      }, 420);
    }
    return undefined;
  };

  return (
    <OwnerVoiceOperatorProvider actions={voiceActions}>
      <Box sx={{ pb: 5, maxWidth: 1660, mx: "auto" }}>
        <OwnerWorkspaceNavigation />

        <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" alignItems={{ lg: "center" }} spacing={1} sx={{ mb: 1.05 }}>
          <Stack direction="row" spacing={.8} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 1000, letterSpacing: "-.035em" }}>Panel rápido</Typography>
            <Chip size="small" color={statusColor} label={statusLabel} sx={{ fontWeight: 950 }} />
          </Stack>
          <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap>
            <OwnerSetupOnboarding center={center} assistant={assistant} onAction={onboardingAction} />
            <Button size="small" startIcon={<PersonAddAltRoundedIcon />} onClick={() => setDialog("client")} sx={compactActionSx}>Persona</Button>
            <Button size="small" startIcon={<AddTaskRoundedIcon />} onClick={() => setDialog("commitment")} sx={compactActionSx}>Tarea</Button>
            <Button size="small" startIcon={<RequestQuoteRoundedIcon />} onClick={() => setDialog("proposal")} sx={compactActionSx}>Propuesta</Button>
            <Button size="small" startIcon={<AccountBalanceWalletRoundedIcon />} onClick={() => setDialog("finance")} sx={compactActionSx}>Movimiento</Button>
            <Button size="small" startIcon={<RepeatRoundedIcon />} onClick={() => setDialog("recurring")} sx={compactActionSx}>Mensual</Button>
            <Button size="small" startIcon={<WorkspacesRoundedIcon />} onClick={() => setDialog("initiative")} sx={compactActionSx}>Proyecto</Button>
            <Button size="small" startIcon={<UploadFileRoundedIcon />} onClick={() => setDialog("import")} sx={compactActionSx}>Importar</Button>
          </Stack>
        </Stack>

        <Box ref={voicePanelAnchorRef}>
          <OwnerVoiceOperatorPanel />
        </Box>
        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}

        <Box sx={{ ...surfaceSx, p: { xs: 1.4, lg: 1.65 }, mb: 1 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}>
            <Box><Typography sx={{ fontWeight: 1000, fontSize: 13, color: "text.secondary", textTransform: "uppercase", letterSpacing: ".08em" }}>Proyectos activos</Typography><Typography sx={{ mt: .2, color: "text.secondary", fontSize: 11.5 }}>Abrí uno para ver objetivo, siguiente paso, dinero, horas, contrato y WebProfit conectados.</Typography></Box>
            <Button size="small" variant="contained" startIcon={<WorkspacesRoundedIcon />} onClick={() => setDialog("initiative")} sx={compactActionSx}>Nuevo proyecto</Button>
          </Stack>
          {initiatives.length ? <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))", xl: "repeat(3,minmax(0,1fr))" }, gap: .75, mt: 1 }}>
            {initiatives.slice(0, 6).map((item) => <Button key={idOf(item)} variant="text" onClick={() => setSelectedInitiative(item)} sx={{ justifyContent: "flex-start", textAlign: "left", textTransform: "none", px: 1, py: .8, border: "1px solid", borderColor: "divider", borderRadius: 2, minWidth: 0 }}><Box sx={{ minWidth: 0, width: "100%" }}><Stack direction="row" justifyContent="space-between" spacing={1}><Typography noWrap sx={{ fontWeight: 950, fontSize: 13 }}>{item.name}</Typography>{item.priority === "high" ? <Chip size="small" color="warning" label="Alta" sx={{ height: 19, fontSize: 10, fontWeight: 900 }} /> : null}</Stack><Typography noWrap sx={{ mt: .2, color: "text.secondary", fontSize: 11.5 }}>{item.nextStep || item.outcome || "Sin próximo paso"}</Typography></Box></Button>)}
          </Box> : <EmptyLine>Sin proyectos activos. Creá uno cuando un trabajo necesite continuidad más allá de una tarea.</EmptyLine>}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1.2fr) minmax(320px,.8fr) minmax(330px,.8fr)" }, gap: 1, alignItems: "stretch" }}>
          <Box sx={{ ...surfaceSx, p: { xs: 1.5, lg: 1.8 }, minHeight: 205 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography sx={{ fontWeight: 1000, fontSize: 13, color: "text.secondary", textTransform: "uppercase", letterSpacing: ".08em" }}>Ahora</Typography>
              {primary?.dueAt || primary?.scheduledStartAt ? <Chip size="small" label={relativeDate(primary.scheduledStartAt || primary.dueAt)} color={isOverdue(primary) ? "warning" : "default"} sx={{ fontWeight: 900 }} /> : null}
            </Stack>
            {primary ? (
              <>
                <Typography sx={{ mt: 1.15, fontWeight: 1000, fontSize: { xs: 22, lg: 26 }, lineHeight: 1.14, letterSpacing: "-.035em" }}>{primary.title}</Typography>
                {primaryPerson ? <Button size="small" onClick={() => openPerson(primaryPerson)} sx={{ mt: .45, p: 0, minWidth: 0, textTransform: "none", fontWeight: 900 }}>{primaryPerson.name}</Button> : null}
                <Stack direction="row" spacing={.7} sx={{ mt: 1.55 }}>
                  <Button size="small" variant="contained" startIcon={<CheckCircleRoundedIcon />} onClick={() => completeCommitment(primary)} sx={compactActionSx}>Hecho</Button>
                  <Button size="small" endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate("/dashboard/owner/calendario")} sx={compactActionSx}>Agenda</Button>
                </Stack>
              </>
            ) : currentQuestion ? (
              <>
                <Typography sx={{ mt: 1.15, fontWeight: 1000, fontSize: { xs: 20, lg: 24 }, lineHeight: 1.18, letterSpacing: "-.03em" }}>{currentQuestion.text}</Typography>
                <Button size="small" variant="contained" startIcon={<AddTaskRoundedIcon />} onClick={() => setDialog("commitment")} sx={{ ...compactActionSx, mt: 1.4 }}>Crear próximo paso</Button>
              </>
            ) : (
              <EmptyLine>No hay nada urgente cargado.</EmptyLine>
            )}
          </Box>

          <Box sx={{ ...surfaceSx, p: { xs: 1.5, lg: 1.8 }, minHeight: 205 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontWeight: 1000, fontSize: 13, color: "text.secondary", textTransform: "uppercase", letterSpacing: ".08em" }}>Después</Typography>
              <Button size="small" onClick={() => navigate("/dashboard/owner/calendario")} sx={{ ...compactActionSx, minWidth: 0 }}>Ver agenda</Button>
            </Stack>
            <Stack divider={<Divider flexItem />} sx={{ mt: .55 }}>
              {nextItems.length ? nextItems.map((item) => {
                const person = peopleById.get(idOf(item.clientId));
                return (
                  <Stack key={idOf(item)} direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ py: .75 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 900, fontSize: 13.5 }}>{item.title}</Typography>
                      <Typography noWrap sx={{ color: isOverdue(item) ? "warning.main" : "text.secondary", fontWeight: 750, fontSize: 11.5 }}>
                        {relativeDate(item.scheduledStartAt || item.dueAt)}{person ? ` · ${person.name}` : ""}
                      </Typography>
                    </Box>
                    <Button size="small" onClick={() => completeCommitment(item)} sx={{ minWidth: 0, px: .7, fontWeight: 950 }}>✓</Button>
                  </Stack>
                );
              }) : <EmptyLine>Sin próximos pasos cargados.</EmptyLine>}
            </Stack>
          </Box>

          <Box sx={{ ...surfaceSx, p: { xs: 1.5, lg: 1.8 }, minHeight: 205 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography sx={{ fontWeight: 1000, fontSize: 13, color: "text.secondary", textTransform: "uppercase", letterSpacing: ".08em" }}>Cómo va</Typography>
              <Stack direction="row" spacing={.35}>
                {(assistant?.currencies || [currency]).slice(0, 3).map((item) => (
                  <Button key={item} size="small" variant={item === currency ? "contained" : "text"} onClick={() => setAssistantCurrency(item)} sx={{ minWidth: 42, px: .7, py: .15, fontWeight: 950, borderRadius: 1.5 }}>{item}</Button>
                ))}
              </Stack>
            </Stack>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, mt: 1.2 }}>
              <MiniStat label="Disponible" value={money(available, currency)} />
              <MiniStat label="Este mes" value={money(month.net, currency)} tone={Number(month.net || 0) < 0 ? "warning.main" : "success.main"} />
              <MiniStat label="Por cobrar" value={money(outstanding, currency)} />
              <MiniStat label="Brecha meta" value={money(gap, currency)} tone={gap > 0 ? "warning.main" : "success.main"} />
            </Box>
            <Divider sx={{ my: 1.2 }} />
            <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap>
              <Typography sx={{ color: overdueCount ? "warning.main" : "text.secondary", fontWeight: 850, fontSize: 12 }}>{overdueCount} vencidos</Typography>
              <Typography sx={{ color: "text.secondary", fontWeight: 850, fontSize: 12 }}>{attentionCount} clientes</Typography>
              <Typography sx={{ color: "text.secondary", fontWeight: 850, fontSize: 12 }}>{inboxCount} inbox</Typography>
            </Stack>
            <Stack direction="row" spacing={.55} sx={{ mt: 1.25 }}>
              {outstanding > 0 ? <Button size="small" startIcon={<PaymentsRoundedIcon />} onClick={() => navigate("/dashboard/owner/clientes")} sx={compactActionSx}>Cobrar</Button> : null}
              <Button size="small" startIcon={<TrendingUpRoundedIcon />} onClick={() => navigate("/dashboard/owner/estadisticas?vista=futuro")} sx={compactActionSx}>Futuro</Button>
            </Stack>
          </Box>
        </Box>

        <OwnerPersonDrawer
          open={Boolean(selectedPerson)}
          person={selectedPerson}
          context={personContext}
          loading={personLoading}
          error={personError}
          onClose={() => { setSelectedPerson(null); setPersonContext(null); setPersonError(""); }}
          onInteraction={(person) => { setRelationPerson(person); setDialog("interaction"); }}
          onFollowUp={(person) => { setRelationPerson(person); setDialog("commitment"); }}
          onExportChatGpt={(person) => setChatGptPerson(person)}
          onMonthly={(person) => { setRelationPerson(person); setDialog("recurring"); }}
          onProposal={(person) => { setRelationPerson(person); setDialog("proposal"); }}
          onProject={(person) => { setRelationPerson(person); setDialog("initiative"); }}
        />

        <OwnerInitiativeDrawer open={Boolean(selectedInitiative)} initiative={selectedInitiative} onClose={() => setSelectedInitiative(null)} onChanged={async (next) => { setSelectedInitiative(next); await load(); }} />
        <OwnerChatGptExportDialog open={Boolean(chatGptPerson)} person={chatGptPerson} onClose={() => setChatGptPerson(null)} />
        <OwnerProposalQuickDialog open={dialog === "proposal"} onClose={closeDialogs} onSaved={refreshAfterAction} preferredClient={relationPerson} />
        <OwnerAccountDialog open={dialog === "account"} onClose={closeDialogs} onSaved={refreshAfterAction} />
        <OwnerFinanceDialog open={dialog === "finance"} onClose={closeDialogs} accounts={center?.finance?.accounts || []} onSaved={refreshAfterAction} />
        <OwnerClientDialog open={dialog === "client"} onClose={closeDialogs} onSaved={refreshAfterAction} />
        <OwnerCommitmentDialog open={dialog === "commitment"} onClose={closeDialogs} onSaved={refreshAfterAction} sourceEntry={sourceEntry} initiatives={center?.life?.initiatives || []} person={relationPerson} />
        <OwnerInteractionDialog open={dialog === "interaction"} onClose={closeDialogs} onSaved={refreshAfterAction} person={relationPerson} />
        <OwnerInitiativeDialog open={dialog === "initiative"} onClose={closeDialogs} onSaved={refreshAfterAction} people={assistant?.clients || center?.people?.focus || []} preferredPerson={relationPerson} />
        <OwnerCollectDialog open={dialog === "collect"} onClose={closeDialogs} sale={saleToCollect} accounts={center?.finance?.accounts || []} onSaved={refreshAfterAction} />
        <OwnerMovementImportDialog open={dialog === "import"} onClose={closeDialogs} onCommitted={refreshAfterAction} />
        <OwnerRecurringFinanceDialog open={dialog === "recurring"} onClose={closeDialogs} accounts={center?.finance?.accounts || []} onSaved={refreshAfterAction} preferredClient={relationPerson} />
        <OwnerRecurringRecordDialog open={dialog === "recurringRecord"} onClose={closeDialogs} plan={recurringToRecord} accounts={center?.finance?.accounts || []} onSaved={refreshAfterAction} />
        <OwnerRecurringFromTransactionDialog open={dialog === "recurringFromTransaction"} onClose={closeDialogs} transaction={transactionToRecurring} recurring={center?.finance?.recurring} onSaved={refreshAfterAction} />
      </Box>
    </OwnerVoiceOperatorProvider>
  );
}
